import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';
import { State } from 'app/gamma/src/modules/types';
import { getMyTeams } from 'app/gamma/src/selectors/teams';
import {
  ExpandableNavigationItem,
  ExpandableNavigationItemLink,
  ExpandableNavigationItemIcon,
  ExpandableNavigationItemLogo,
  ExpandableNavigationItemText,
  ExpandableNavigationItemList,
  ExpandableNavigationItemButtonProps,
} from 'app/src/components/ExpandableNavigation';
import { TeamModel, EnterpriseModel } from 'app/gamma/src/types/models';
import { TeamTabProps, TeamLinkSubTab } from './team-tab';
import { HomeTestIds } from '@trello/webdriverio-tests';
import { CreateTeamButton } from './create-team-button';
import { SectionHeader } from './section-header';
import { UpgradePromptFull } from 'app/src/components/UpgradePrompts';
import { useUpgradePrompt } from 'app/src/components/UpgradePrompts/useUpgradePrompt';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { logoDomain } from '@trello/config';
import { featureFlagClient, useFeatureFlag } from '@trello/feature-flag-client';
import { IconName } from 'app/common/components/controls';
import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial/useFreeTrialEligibilityRules';

import styles from './team-list.less';

import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';
const l = forTemplate('home');
const BC_PROMPT_MESSAGE_ID = 'ad-home-team-nav-tab';

const formatUpgradePrompt = forNamespace('upgrade prompt');
const formatFreeTrial = forNamespace('free trial existing');

interface TeamTabListProps {
  teamTabs: TeamTabProps[];
  createTeamButton: ExpandableNavigationItemButtonProps;
  enterprises: EnterpriseModel[];
}

interface TeamTabListStateProps {
  teams: TeamModel[];
}

interface TeamTabListDispatchProps {}

interface TeamTabSectionProps {
  teamTabs: TeamTabProps[];
  createTeamButton: ExpandableNavigationItemButtonProps;
  sectionTitle: string;
  showCreateTeamButton: boolean;
  noFixedHeight?: boolean;
}

type AllTeamTabListProps = TeamTabListProps &
  TeamTabListStateProps &
  TeamTabListDispatchProps;

const TeamTab: React.FunctionComponent<TeamTabProps> = ({
  billingUrl,
  displayName,
  expanded,
  orgId,
  linkTabs,
  logoUrl1x,
  logoUrl2x,
  selected,
  idEnterprise,
  ...props
}) => {
  const {
    dismissMessage,
    hidePrompt,
    shouldDisplayUpgradePrompt,
  } = useUpgradePrompt({ orgId, messageId: BC_PROMPT_MESSAGE_ID });

  // TRELP-2437 Free trial eligibility should only be requested if the upgrade prompt will be rendered
  const shouldQueryFreeTrialEligibility =
    expanded && shouldDisplayUpgradePrompt;
  const { isEligible } = useFreeTrialEligibilityRules(orgId, {
    skip: !shouldQueryFreeTrialEligibility,
  });

  let headline = formatUpgradePrompt(['home', 'team tab', 'headline-2']);
  let content = formatUpgradePrompt(['home', 'team tab', 'content-2']);

  if (useFeatureFlag('btg.free-trial-existing-teams', false) && isEligible) {
    headline = formatFreeTrial(['upgrade prompt', 'headline']);
    content = formatFreeTrial(['upgrade prompt', 'content']);
  }

  return (
    <ExpandableNavigationItem
      data-test-id={`${HomeTestIds.TeamTabSection}${orgId}`}
    >
      <ExpandableNavigationItemLink selected={selected} {...props}>
        {logoUrl1x && (
          <ExpandableNavigationItemLogo
            logoUrl1x={logoUrl1x}
            logoUrl2x={logoUrl2x}
          />
        )}
        {!logoUrl1x && <ExpandableNavigationItemIcon iconName="organization" />}
        <ExpandableNavigationItemText data-test-id={HomeTestIds.TeamTabName}>
          {displayName}
        </ExpandableNavigationItemText>
      </ExpandableNavigationItemLink>
      {expanded && !!linkTabs.length && (
        <ExpandableNavigationItemList>
          {linkTabs.map((linkTab) => (
            <TeamLinkSubTab {...linkTab} />
          ))}
        </ExpandableNavigationItemList>
      )}
      {expanded && shouldDisplayUpgradePrompt && (
        <UpgradePromptFull
          isDismissable
          headline={headline}
          content={content}
          iconName={IconName.BusinessClass}
          cta={formatUpgradePrompt('learn more')}
          ctaLink={billingUrl}
          onDismiss={() => {
            dismissMessage();
            hidePrompt();
          }}
          ctaOnClick={() =>
            trackUe({
              category: Noun.HOME_TEAM_ACCORDION,
              verb: Verb.OPENS,
              directObj: Noun.BC_MODAL,
              method:
                'by clicking full upgrade prompt in home page team accordion',
            })
          }
          orgId={orgId}
          source="memberHomeScreen"
        />
      )}
    </ExpandableNavigationItem>
  );
};

const TeamTabSection: React.FunctionComponent<TeamTabSectionProps> = ({
  sectionTitle,
  showCreateTeamButton,
  createTeamButton,
  teamTabs,
  noFixedHeight,
}) => {
  return (
    <>
      <div className={styles.sectionHeaderWrapper}>
        <SectionHeader noMargin noFixedHeight={noFixedHeight}>
          {sectionTitle}
        </SectionHeader>
        {showCreateTeamButton && <CreateTeamButton {...createTeamButton} />}
      </div>
      {teamTabs.map((teamTab) => (
        <TeamTab {...teamTab} />
      ))}
    </>
  );
};

const TeamTabListUnconntected: React.FunctionComponent<AllTeamTabListProps> = ({
  teamTabs,
  teams,
  createTeamButton,
  enterprises,
}) => {
  const enterpriseIds = enterprises.map((e) => e.id);
  const groupedTeams = _.groupBy(teamTabs, (team) => {
    const idEnterprise = team.idEnterprise;
    // Not the most elegant solution, but `_.groupBy` would
    // return an object with `null` as a key otherwise
    // If a team has an idEnterprise but we cannot find
    // its enterprise, it might be BCPO or AtlOrg and
    // we should display it in 'OTHER TEAMS'
    return idEnterprise && enterpriseIds.includes(idEnterprise)
      ? idEnterprise
      : 'non-enterprise';
  });
  const enterpriseSections = enterprises
    .map((ent) => _.extend(ent, { teams: groupedTeams[ent.id] }))
    .filter((ent) => !!ent.teams);
  const isCorpVisEnabled = featureFlagClient.get(
    'enterprise.corporate-visibility',
    false,
  );
  const hasTeams = teams.length > 0;

  return (
    <ExpandableNavigationItemList>
      {isCorpVisEnabled && enterpriseSections.length ? (
        <>
          {enterpriseSections.map((ent) => (
            <React.Fragment key={ent.id}>
              <hr />
              {ent.logoHash && (
                <div className={styles.entLogo}>
                  <img
                    src={`${logoDomain}/${ent.logoHash}/170.png`}
                    alt=""
                    role="presentation"
                    data-test-id={`${HomeTestIds.TeamTabSection}${ent.id}`}
                  />
                </div>
              )}
              <TeamTabSection
                sectionTitle={`${ent.displayName} ${l('teams')}`}
                showCreateTeamButton={hasTeams}
                createTeamButton={createTeamButton}
                teamTabs={ent.teams}
                noFixedHeight={true}
              />
            </React.Fragment>
          ))}
          <hr />
          <TeamTabSection
            sectionTitle={l('other-teams')}
            showCreateTeamButton={hasTeams}
            createTeamButton={createTeamButton}
            teamTabs={groupedTeams['non-enterprise'] || []}
          />
        </>
      ) : (
        <TeamTabSection
          sectionTitle={l('teams')}
          showCreateTeamButton={hasTeams}
          createTeamButton={createTeamButton}
          teamTabs={teamTabs}
        />
      )}
      {!hasTeams && (
        <ExpandableNavigationItem>
          <CreateTeamButton fullTab {...createTeamButton} />
        </ExpandableNavigationItem>
      )}
    </ExpandableNavigationItemList>
  );
};

const mapStateToProps = (state: State): TeamTabListStateProps => ({
  teams: getMyTeams(state),
});

export const TeamTabList = connect(mapStateToProps)(TeamTabListUnconntected);
